import { Component, computed, DestroyRef, inject, input, output, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ButtonModule } from '@openng/optimus-ui/button';
import { Menu, MenuModule } from '@openng/optimus-ui/menu';
import { TagModule } from '@openng/optimus-ui/tag';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { ConfirmDialogModule } from '@openng/optimus-ui/confirmdialog';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ConfirmationService, MenuItem } from '@openng/optimus-ui/api';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbilityService, CurrencyFormatPipe, DropdownData, GenericDropdownComponent } from '@zayan-erp/shared';
import { BuilderReferenceService } from '@zayan-erp/home-building';
import { map } from 'rxjs';
import { DsmDealOption } from '../../../models/dsm-deal-options-view.models';
import {
  DSM_SIGNER_CHIP_CLASSES,
  DSM_SIGNER_CHIP_LABELS,
  DSM_SIGNER_ROLE_LABELS,
  DsmRecipientStatus,
  DsmSignOffSigner
} from '../../../models/dsm-addendum-sign-off.models';
import { DsmDealOptionsService } from '../../../services/dsm-deal-options.service';
import { DsmApsDealContextService } from '../../../services/dsm-aps-deal-context.service';
import { DsmLotIncentiveService } from '../../../services/dsm-lot-incentive.service';
import { DsmAddendumLogsComponent } from '../dsm-addendum-logs/dsm-addendum-logs.component';
import { DsmIncentiveReallocationFormComponent } from '../dsm-incentive-reallocation-form/dsm-incentive-reallocation-form.component';
import { DsmRoomReplacementRerunComponent } from '../dsm-room-replacement-rerun/dsm-room-replacement-rerun.component';
import type { DsmPendingLocationRule } from '../../../models/dsm-room-replacement-rule.models';

@Component({
  selector: 'dsm-aps-addendum-preview',
  standalone: true,
  imports: [
    DsmAddendumLogsComponent,
    ConfirmDialogModule,
    TooltipModule,
    CurrencyFormatPipe,
    DatePipe,
    NgTemplateOutlet,
    ButtonModule,
    MenuModule,
    DialogModule,
    TagModule,
    DsmIncentiveReallocationFormComponent,
    DsmRoomReplacementRerunComponent,
    ReactiveFormsModule,
    GenericDropdownComponent
  ],
  providers: [ConfirmationService],
  templateUrl: './dsm-aps-addendum-preview.component.html'
})
export class DsmApsAddendumPreviewComponent {
  readonly #confirmationService = inject(ConfirmationService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dealOptionsService = inject(DsmDealOptionsService);
  readonly #dealContext = inject(DsmApsDealContextService);
  readonly #lotIncentiveService = inject(DsmLotIncentiveService);
  readonly #abilityService = inject(AbilityService);
  readonly #builderReferenceService = inject(BuilderReferenceService);
  readonly #fb = inject(FormBuilder);

  actionMenu = viewChild<Menu>('actionMenu');

  readonly dealAddendum = input.required<DsmDealOption>();
  readonly isExpanded = input<boolean>(false);

  readonly showSeq = input<boolean>(true);
  readonly showActions = input<boolean>(true);
  readonly showSignOffStatus = input<boolean>(true);
  readonly showExpandToggle = input<boolean>(true);
  readonly showTotal = input<boolean>(true);
  readonly actionRailAlign = input<'top' | 'center'>('top');

  onChanges = output<void>();
  toggleExpand = output<void>();

  logsDialogVisible = signal(false);
  actionMenuItems = signal<MenuItem[]>([]);
  isReallocationModalVisible = signal(false);
  isEditBuilderReferenceModalVisible = signal(false);
  isRoomReplacementRerunModalVisible = signal(false);
  isSubmittingBuilderReference = signal(false);
  readonly freedIncentiveAmount = signal<number>(0);

  readonly builderReferences = toSignal(
    this.#builderReferenceService
      .getBuilderReferences()
      .pipe(map(references => references.map<DropdownData>(ref => ({ id: ref.id, firstLabel: ref.reference })))),
    { initialValue: [] }
  );

  readonly builderReferenceForm = this.#fb.group({
    builderReference: [null as string | null, Validators.required]
  });

  readonly dealId = computed(() => this.#dealContext.effectiveDealId());
  readonly discountsTotal = computed(() => this.dealAddendum().discountsTotal ?? 0);
  readonly optionsTotal = computed(() => this.dealAddendum().optionsTotal ?? 0);
  readonly incentivesTotal = computed(() => this.dealAddendum().incentivesTotal ?? 0);
  readonly taxesTotal = computed(() => this.dealAddendum().taxesTotal ?? 0);
  readonly isLocked = computed(() => this.dealAddendum().isLocked);

  readonly isBothApproved = computed(() => this.dealAddendum().buyerApproved && this.dealAddendum().builderApproved);
  readonly showLocked = computed(() => this.isLocked() || this.isBothApproved());
  readonly createdByName = computed(() => this.dealAddendum().createdByUserFullName?.trim() || '');
  readonly createdOn = computed(() => this.dealAddendum().createdAt ?? this.dealAddendum().documentDate ?? null);
  readonly lastEditedOn = computed(() => this.dealAddendum().lastModified ?? null);
  readonly signOffSigners = computed<DsmSignOffSigner[]>(() =>
    [...(this.dealAddendum().signOff?.signers ?? [])].sort((a, b) => a.sequence - b.sequence)
  );
  readonly signOffCompletionDate = computed(() => this.dealAddendum().signOff?.completedAt ?? null);
  readonly allSigned = computed(() => {
    const signers = this.signOffSigners();
    return signers.length > 0 && signers.every(signer => signer.status === DsmRecipientStatus.Complete);
  });
  readonly signerRoleLabels = DSM_SIGNER_ROLE_LABELS;
  readonly signerChipLabels = DSM_SIGNER_CHIP_LABELS;
  readonly signerChipClasses = DSM_SIGNER_CHIP_CLASSES;

  readonly canDelete = computed(() => !this.isLocked());
  readonly canCancel = computed(
    () =>
      this.isLocked() &&
      !this.dealAddendum().isCancelled &&
      this.#abilityService.ability.can('dsm-cancel-locked-addendum', 'aps')
  );
  // SuperAdmin-only when locked; otherwise available to anyone who can edit the addendum.
  readonly canEditBuilderReference = computed(
    () =>
      !this.dealAddendum().isCancelled &&
      (!this.isLocked() || this.#abilityService.ability.can('dsm-edit-locked-addendum', 'aps'))
  );
  // Rules run when an addendum locks, so re-running them only means something once it has.
  readonly canRerunRoomReplacementRules = computed(
    () =>
      this.isLocked() &&
      !this.dealAddendum().isCancelled &&
      this.#abilityService.ability.can('dsm-rerun-room-replacement-rules', 'aps')
  );
  readonly hasActionMenu = computed(() => this.canRerunRoomReplacementRules() || this.canCancel() || this.canDelete());

  openActionMenu(event: Event): void {
    const items: MenuItem[] = [];
    if (this.canRerunRoomReplacementRules()) {
      items.push({
        label: 'Apply Location Rules',
        command: () => this.isRoomReplacementRerunModalVisible.set(true)
      });
    }
    if (this.canCancel()) {
      items.push({
        label: 'Cancel Addendum',
        linkClass: 'text-red-600',
        command: () => this.showConfirmation('cancel')
      });
    }
    if (this.canDelete()) {
      items.push({
        label: 'Delete',
        linkClass: 'text-red-600',
        command: () => this.showConfirmation('delete')
      });
    }
    this.actionMenuItems.set(items);
    this.actionMenu()?.toggle(event);
  }

  openOptionLogsModal(): void {
    this.logsDialogVisible.set(true);
  }

  openEditBuilderReferenceModal(event: Event): void {
    event.stopPropagation();
    this.builderReferenceForm.patchValue({
      builderReference: this.dealAddendum().builderReference.id
    });
    this.isEditBuilderReferenceModalVisible.set(true);
  }

  closeEditBuilderReferenceModal(): void {
    this.isEditBuilderReferenceModalVisible.set(false);
  }

  saveBuilderReference(): void {
    if (this.builderReferenceForm.invalid) {
      this.builderReferenceForm.markAllAsTouched();
      return;
    }

    const addendum = this.dealAddendum();
    const builderReferenceId = this.builderReferenceForm.controls.builderReference.value;
    if (!builderReferenceId) {
      return;
    }

    if (builderReferenceId === addendum.builderReference.id) {
      this.closeEditBuilderReferenceModal();
      return;
    }

    this.isSubmittingBuilderReference.set(true);
    this.#dealOptionsService
      .updateBuilderReference(addendum.id, builderReferenceId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.isSubmittingBuilderReference.set(false);
          this.closeEditBuilderReferenceModal();
          this.onChanges.emit();
        }
      });
  }

  showConfirmation(action: 'cancel' | 'delete'): void {
    const isDelete = action === 'delete';
    this.#confirmationService.confirm({
      header: isDelete ? 'Delete Addendum?' : 'Cancel Addendum?',
      message: isDelete
        ? 'Are you sure you want to delete the Addendum?'
        : 'Do you want to cancel this addendum? This action is permanent and cannot be undone.',
      acceptLabel: isDelete ? 'Delete' : 'Cancel Addendum',
      rejectLabel: 'No',
      acceptButtonProps: { severity: 'danger' },
      rejectButtonProps: {
        severity: 'secondary',
        outlined: false
      },
      accept: () => {
        isDelete ? this.deleteOption() : this.cancelAddendum();
      }
    });
  }

  deleteOption(): void {
    this.#dealOptionsService
      .deleteOption(this.dealAddendum().id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.#lotIncentiveService.reloadLotIncentive();
          this.onChanges.emit();
        }
      });
  }

  cancelAddendum(): void {
    const freedAmount = this.dealAddendum().incentivesTotal ?? 0;
    this.freedIncentiveAmount.set(freedAmount);

    this.#dealOptionsService
      .cancelAddendum(this.dealAddendum().id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          if (freedAmount <= 0) {
            this.#lotIncentiveService.reloadLotIncentive();
            this.#dealContext.reloadDeal.set(true);
            this.onChanges.emit();
            return;
          }
          this.openReallocationModal();
        }
      });
  }

  openReallocationModal(): void {
    this.isReallocationModalVisible.set(true);
  }

  closeReallocationModal(): void {
    this.isReallocationModalVisible.set(false);
    this.#lotIncentiveService.reloadLotIncentive();
    this.#dealContext.reloadDeal.set(true);
    this.onChanges.emit();
  }

  onReallocationSaved(): void {
    this.closeReallocationModal();
  }

  onReallocationDismiss(): void {
    this.closeReallocationModal();
  }

  openRoomReplacementRerunModal(): void {
    this.isRoomReplacementRerunModalVisible.set(true);
  }

  closeRoomReplacementRerunModal(): void {
    this.isRoomReplacementRerunModalVisible.set(false);
  }

  // Cancelled lines change totals and the incentive pool, and the parent's review reload brings up
  // the mismatch banners for any unlocked lines left in replaced rooms.
  onRoomReplacementRerun(): void {
    this.closeRoomReplacementRerunModal();
    this.#lotIncentiveService.reloadLotIncentive();
    this.#dealContext.reloadDeal.set(true);
    this.onChanges.emit();
  }
}
